import { Seller } from '../types/invoiceTypes';

/**
 * `hasSellerRequiredFields` - M-UX-7.3 (`plan-ux-7.3-advisor-invoice.md` §2.2, E5).
 *
 * Pure helper: reports whether a {@link Seller} record carries the minimal
 * address/company fields needed to actually invoice under that seller's own
 * name. Field set is EXACT parity with the onboarding-wizard gate in
 * `components/layout/MainLayout.tsx:144-150` (`hasRequiredFields` local
 * const). This anchor is intentionally ONE-SIDED: `MainLayout.tsx` is NOT
 * touched by this module (E5 - Null-Delta am Hot-File `MainLayout.tsx`,
 * verified by the M-UX-7.3 regression self-test: `git diff` must show zero
 * change there). Keep this field set in sync manually if `hasRequiredFields`
 * in `MainLayout.tsx` ever changes. Consumers: `KanzleiProfileCard.tsx` and —
 * M-UX-Cockpit-Home (`plan-ux-table-cockpit-quality.md` §B) — the cockpit
 * activation checklist (`pages/CockpitPage.tsx`), which uses it to make the
 * `profileComplete` step reflect REAL required fields instead of the
 * `onboardingCompleted` wizard proxy.
 *
 * No Firebase import, no side effects - safe to unit-test in isolation.
 *
 * @param sellerData the seller record to check, or `null`/`undefined` while
 *   not yet loaded / not present.
 * @returns `true` iff `name`, `street`, `zip`, `city` and `country` are all
 *   present and non-empty.
 * @module utils/sellerProfile
 */
export function hasSellerRequiredFields(sellerData: Seller | null | undefined): boolean {
  return !!(
    sellerData?.name &&
    sellerData?.street &&
    sellerData?.zip &&
    sellerData?.city &&
    sellerData?.country
  );
}

/**
 * `hasSellerTaxIdentification` - § 14 Abs. 4 Nr. 2 UStG (#339).
 *
 * Reports whether the seller carries at least one tax identifier
 * (Steuernummer `taxNumber` OR USt-IdNr. `vatNumber`). Required for EVERY
 * seller issuing an invoice (regular taxation and Kleinunternehmer alike).
 * Whitespace-only values do not count. Offers are not § 14 documents and
 * must not use this check.
 *
 * @param sellerData the seller record, or `null`/`undefined` while not loaded.
 * @returns `true` iff `taxNumber` or `vatNumber` is a non-blank string.
 */
export function hasSellerTaxIdentification(sellerData: Seller | null | undefined): boolean {
  const hasValue = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '';
  return hasValue(sellerData?.taxNumber) || hasValue(sellerData?.vatNumber);
}
